/**
 * Gate for "background-freshness" sync triggers (window focus / tab wake).
 *
 * These triggers should coalesce: firing several within a few seconds wastes a
 * full pull each time. The naive gate compares against the last *completed*
 * sync — but a completion timestamp lags while a sync is slow or in-flight, so
 * piled-up triggers all see "stale" and fire anyway (observed in the list window
 * as focus/wake syncs <1s apart during an edit burst).
 *
 * Keying on the most recent of {last attempt, last completion} fixes that: an
 * attempt that hasn't finished yet still throttles the next trigger. Never drops
 * a needed sync — the interval sync and the next genuine trigger always catch up.
 *
 * @returns true if enough time has elapsed that a background sync should run.
 */
export function shouldRunBackgroundSync(
  nowMs: number,
  lastAttemptMs: number,
  lastCompletedMs: number | null,
  thresholdMs: number,
): boolean {
  const last = Math.max(lastAttemptMs, lastCompletedMs ?? 0)
  return nowMs - last >= thresholdMs
}

/**
 * Generic time-throttle gate: true once `thresholdMs` has elapsed since `lastMs`.
 * Collapses a burst of repeated work into at most one run per window — e.g. the
 * `is_active` profiles check that Supabase re-fires on every window focus /
 * TOKEN_REFRESHED, observed as `profiles ×10` on a single refocus. Pass an
 * epoch timestamp (`Date.now()`); `lastMs = 0` ("never") then always elapses.
 */
export function isThrottleElapsed(nowMs: number, lastMs: number, thresholdMs: number): boolean {
  return nowMs - lastMs >= thresholdMs
}
